import React, { useEffect, useState } from 'react';
import { Download, X, Share, Plus } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { trackPwaEvent } from '../lib/pwaAnalytics';

const DISMISS_KEY = 'allmx_pwa_install_dismissed_v1';
const DISMISS_DAYS = 14;

const isIOS = () =>
  typeof navigator !== 'undefined' &&
  /iPad|iPhone|iPod/.test(navigator.userAgent) &&
  !window.MSStream;

const isStandalone = () =>
  typeof window !== 'undefined' &&
  (window.matchMedia?.('(display-mode: standalone)').matches ||
    window.navigator.standalone === true);

const InstallPWAPrompt = () => {
  const { language } = useLanguage();
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [iosBanner, setIosBanner] = useState(false);

  const t = {
    it: {
      title: 'Installa l\'app ALLMX',
      desc: 'Aggiungi ALLMX alla schermata home per accedere più velocemente al catalogo.',
      install: 'Installa',
      later: 'Più tardi',
      iosTitle: 'Installa ALLMX sul tuo iPhone',
      iosStep1: 'Tocca il pulsante',
      iosStep2: 'in basso, poi',
      iosStep3: '"Aggiungi a Home"',
    },
    en: {
      title: 'Install the ALLMX app',
      desc: 'Add ALLMX to your home screen for faster access to the catalog.',
      install: 'Install',
      later: 'Later',
      iosTitle: 'Install ALLMX on your iPhone',
      iosStep1: 'Tap the',
      iosStep2: 'button below, then',
      iosStep3: '"Add to Home Screen"',
    },
    es: {
      title: 'Instala la app ALLMX',
      desc: 'Añade ALLMX a tu pantalla de inicio para un acceso más rápido al catálogo.',
      install: 'Instalar',
      later: 'Más tarde',
      iosTitle: 'Instala ALLMX en tu iPhone',
      iosStep1: 'Toca el botón',
      iosStep2: 'abajo y luego',
      iosStep3: '"Añadir a pantalla de inicio"',
    },
  };
  const tr = t[language] || t.it;

  // Detect if user already dismissed recently or already installed
  const shouldSuppress = () => {
    if (isStandalone()) return true;
    const dismissedAt = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    if (!dismissedAt) return false;
    return Date.now() - dismissedAt < DISMISS_DAYS * 24 * 60 * 60 * 1000;
  };

  useEffect(() => {
    // Always listen for the browser-confirmed installation event (fires even
    // if the user installs via browser menu instead of our banner).
    const onInstalled = () => trackPwaEvent('installed');
    window.addEventListener('appinstalled', onInstalled);

    // Fire once-per-day if the page itself is currently loaded as a standalone
    // PWA. This is the ONLY reliable way to count iOS users since iOS Safari
    // never fires `appinstalled`.
    if (isStandalone()) {
      const LAST_LAUNCH_KEY = 'allmx_pwa_last_launch_day';
      const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
      if (localStorage.getItem(LAST_LAUNCH_KEY) !== today) {
        localStorage.setItem(LAST_LAUNCH_KEY, today);
        trackPwaEvent('pwa_launch');
      }
    }

    if (shouldSuppress()) {
      return () => window.removeEventListener('appinstalled', onInstalled);
    }

    // Chrome / Edge / Android
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      // Delay the banner so it doesn't pop up immediately on first paint
      setTimeout(() => {
        setShowPrompt(true);
        trackPwaEvent('install_prompt_shown');
      }, 3000);
    };
    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // iOS Safari has no beforeinstallprompt — show manual instructions banner
    if (isIOS()) {
      setTimeout(() => {
        setIosBanner(true);
        trackPwaEvent('install_prompt_shown');
      }, 4000);
    }

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    trackPwaEvent(choice?.outcome === 'accepted' ? 'install_accepted' : 'install_dismissed');
    setDeferredPrompt(null);
    setShowPrompt(false);
  };

  const handleDismiss = () => {
    localStorage.setItem(DISMISS_KEY, Date.now().toString());
    trackPwaEvent('install_dismissed');
    setShowPrompt(false);
    setIosBanner(false);
  };

  // --- Chrome/Android prompt ---
  if (showPrompt && deferredPrompt) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 md:left-auto md:right-6 md:bottom-6 md:max-w-sm z-[70] bg-black border border-white/15 rounded-xl shadow-2xl p-4 animate-fade-in"
        data-testid="pwa-install-banner"
      >
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Chiudi"
          className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 text-white/60 hover:text-white"
        >
          <X size={16} />
        </button>
        <div className="flex items-start gap-3 pr-6">
          <div className="flex-shrink-0 w-12 h-12 rounded-lg bg-[#FF6600]/15 flex items-center justify-center">
            <Download size={22} style={{ color: '#FF6600' }} />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold text-sm">{tr.title}</h3>
            <p className="text-white/60 text-xs mt-1">{tr.desc}</p>
            <div className="flex gap-2 mt-3">
              <button
                type="button"
                onClick={handleInstall}
                className="px-4 py-1.5 rounded text-xs font-semibold text-white"
                style={{ background: '#FF6600' }}
                data-testid="pwa-install-confirm"
              >
                {tr.install}
              </button>
              <button
                type="button"
                onClick={handleDismiss}
                className="px-4 py-1.5 rounded text-xs text-white/70 hover:text-white hover:bg-white/10"
              >
                {tr.later}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // --- iOS manual-instructions banner ---
  if (iosBanner) {
    return (
      <div
        className="fixed bottom-4 left-4 right-4 z-[70] bg-black border border-white/15 rounded-xl shadow-2xl p-4 animate-fade-in"
        data-testid="pwa-install-banner-ios"
      >
        <button
          type="button"
          onClick={handleDismiss}
          aria-label="Chiudi"
          className="absolute top-2 right-2 p-1 rounded hover:bg-white/10 text-white/60 hover:text-white"
        >
          <X size={16} />
        </button>
        <div className="pr-6">
          <h3 className="text-white font-semibold text-sm flex items-center gap-2">
            <Download size={16} style={{ color: '#FF6600' }} />
            {tr.iosTitle}
          </h3>
          <p className="text-white/70 text-xs mt-2 leading-relaxed">
            {tr.iosStep1} <Share size={14} className="inline mx-1 text-blue-400" /> {tr.iosStep2}{' '}
            <Plus size={14} className="inline mx-1 text-white" />
            <span className="font-semibold text-white"> {tr.iosStep3}</span>
          </p>
        </div>
      </div>
    );
  }

  return null;
};

export default InstallPWAPrompt;
